import { useEffect, useState } from "react";
import { GooglePlayLogo, X, Sparkle } from "@phosphor-icons/react";
import { GOOGLE_PLAY_URL } from "@/components/GooglePlayBadge";

const DISMISS_KEY = "nestled.gp-ribbon.dismissed.v1";

/**
 * Site-wide "Nestled is now on Google Play" ribbon rendered at the very top of
 * every marketing page. Dismissable — the user's preference persists across
 * pages and sessions via localStorage.
 */
export default function AppDownloadRibbon() {
  const [dismissed, setDismissed] = useState(true); // start true to avoid SSR flash

  useEffect(() => {
    try {
      setDismissed(!!localStorage.getItem(DISMISS_KEY));
    } catch {
      setDismissed(false);
    }
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="app-download-ribbon"
      className="relative bg-gradient-to-r from-stoneink-900 via-stoneink-900 to-terracotta-600 text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-2.5 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] shrink-0">
          <Sparkle weight="fill" size={12} className="text-sage-400" /> New
        </span>
        <p className="text-sm sm:text-[15px] font-medium flex-1 min-w-0 truncate">
          Nestled is now live on Google Play &mdash; take your home with you.
        </p>
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="app-download-ribbon-cta"
          className="inline-flex items-center gap-2 h-8 pl-2.5 pr-3.5 rounded-lg bg-white text-stoneink-900 text-xs sm:text-sm font-semibold hover:bg-sand-100 transition-colors active:scale-[0.98] shrink-0"
        >
          <GooglePlayLogo weight="fill" size={16} className="text-stoneink-900" />
          <span className="hidden sm:inline">Get it on Google Play</span>
          <span className="sm:hidden">Download</span>
        </a>
        <button
          onClick={dismiss}
          data-testid="app-download-ribbon-dismiss"
          aria-label="Dismiss download banner"
          className="h-8 w-8 grid place-items-center rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors shrink-0"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
